import React, { useEffect, useRef, useState } from "react";
import axios from "../api/client";
import { fetchFuelTypes } from "../api/client";
import { useAuth } from "../context/AuthContext.jsx";
import { useTranslation } from "react-i18next";

const CORPORATE_VARIANT_IDS = new Set([
  "bensin_95",
  "bensin_98",
  "diesel",
  "diesel_winter",
  "diesel_eco",
  "burning_oil_summer",
  "burning_oil_winter",
  "burning_oil_eco",
]);

const SITE_LABELS = {
  neste_corp: "Neste",
  st1_for_corporate: "St1",
  abc_corp: "ABC",
};

const ABC_RECOVERY_REASONS = new Set([
  "otp_required",
  "otp_timeout",
  "otp_invalid",
  "phone_required",
]);

export default function ComparePage() {
  const { user, isAuthenticated, authLoading } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const pageTitle = t("compare.title");
  const [postalInput, setPostalInput] = useState("");
  const [litres, setLitres] = useState("");
  const [data, setData] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [fuelGroups, setFuelGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedFuelType, setSelectedFuelType] = useState("");
  const [fuelTypeLoadError, setFuelTypeLoadError] = useState("");
  const [profit, setProfit] = useState("0.000");
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcodeValue, setPasscodeValue] = useState("");
  const [passcodeError, setPasscodeError] = useState("");
  const [passcodeSuccess, setPasscodeSuccess] = useState("");
  const [passcodeSubmitting, setPasscodeSubmitting] = useState(false);
  const [passcodeResetting, setPasscodeResetting] = useState(false);
  const [passcodeResetStatus, setPasscodeResetStatus] = useState("");
  const [passcodeSessionStatus, setPasscodeSessionStatus] = useState("");
  const [nesteRenewal, setNesteRenewal] = useState(null);
  const [showAbcModal, setShowAbcModal] = useState(false);
  const [abcPhone, setAbcPhone] = useState("");
  const [abcOtp, setAbcOtp] = useState("");
  const [abcPhoneStatus, setAbcPhoneStatus] = useState("");
  const [abcOtpStatus, setAbcOtpStatus] = useState("");
  const [abcError, setAbcError] = useState("");
  const [abcLoading, setAbcLoading] = useState(false);
  const [abcBatchMessage, setAbcBatchMessage] = useState("");
  const [abcBatchLoading, setAbcBatchLoading] = useState(false);
  const [pendingAbcBatch, setPendingAbcBatch] = useState(false);
  const [abcBatchStatus, setAbcBatchStatus] = useState(null);
  const [abcBatchProgress, setAbcBatchProgress] = useState("");
  const abcBatchTimer = useRef(null);
  const comparisonRunTimer = useRef(null);
  const [comparisonRun, setComparisonRun] = useState(null);
  const [comparisonClock, setComparisonClock] = useState(Date.now());

  function shouldOpenAbcFromProviderStatuses(providerStatuses = []) {
    const abcStatus = providerStatuses.find((entry) => entry?.site === "abc_corp");
    return abcStatus?.state === "failed" && ABC_RECOVERY_REASONS.has(abcStatus?.reason);
  }

  function formatProviderList(providerNames = []) {
    const uniqueNames = [...new Set(providerNames.filter(Boolean))];
    if (uniqueNames.length <= 1) {
      return uniqueNames[0] || "";
    }
    if (uniqueNames.length === 2) {
      return `${uniqueNames[0]} ja ${uniqueNames[1]}`;
    }
    return `${uniqueNames.slice(0, -1).join(", ")} ja ${uniqueNames[uniqueNames.length - 1]}`;
  }

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return undefined;
    }

    let ignore = false;
    let retryTimer = null;

    async function loadFuelTypes(allowRetry = true) {
      try {
        setFuelTypeLoadError("");
        let groups = await fetchFuelTypes();
        groups = groups
          .map((group) => ({
            ...group,
            variants: group.variants?.filter((variant) =>
              CORPORATE_VARIANT_IDS.has(variant.id)
            ),
          }))
          .filter((group) => group.variants && group.variants.length);
        if (ignore) return;
        setFuelGroups(groups);

        const flattened = [];
        groups.forEach((group) => {
          group.variants?.forEach((variant) => {
            flattened.push({ ...variant, groupId: group.id });
          });
        });

        const defaultVariant =
          flattened.find((variant) => variant.isDefault) || flattened[0];

        if (defaultVariant) {
          setSelectedFuelType(defaultVariant.id);
          setSelectedGroupId(defaultVariant.groupId);
        } else if (groups.length > 0) {
          setSelectedGroupId(groups[0].id);
          setSelectedFuelType(groups[0].variants?.[0]?.id || "");
        }
      } catch (err) {
        console.error("Failed to load fuel types", err);
        // Suppress UI error; retry logic handles transient 401/refresh cases
        const shouldRetry =
          allowRetry &&
          err?.response?.status === 401 &&
          isAuthenticated &&
          !authLoading;
        if (shouldRetry) {
          retryTimer = setTimeout(() => {
            if (!ignore) {
              loadFuelTypes(false);
            }
          }, 1500);
        }
      }
    }

    loadFuelTypes();

    return () => {
      ignore = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [t, authLoading, isAuthenticated]);

  useEffect(() => {
    if (!showPasscodeModal || !isAdmin) {
      return;
    }
    let ignore = false;
    async function loadNesteSessionStatus() {
      try {
        const res = await axios.get("/api/admin/neste-session/status");
        if (ignore) return;
        if (res.data?.exists) {
          const expiresAt = res.data.expiresAt
            ? new Date(res.data.expiresAt)
            : null;
          const dateLabel = expiresAt
            ? expiresAt.toLocaleDateString("fi-FI")
            : "";
          const daysLeft =
            typeof res.data.daysLeft === "number" ? res.data.daysLeft : null;
          if (daysLeft !== null && dateLabel) {
            setPasscodeSessionStatus(
              t("compare.passcode.sessionStatus", {
                days: daysLeft,
                date: dateLabel,
              })
            );
          } else {
            setPasscodeSessionStatus(t("compare.passcode.sessionMissing"));
          }
        } else {
          setPasscodeSessionStatus(t("compare.passcode.sessionMissing"));
        }
      } catch (err) {
        if (!ignore) {
          setPasscodeSessionStatus("");
        }
      }
    }
    async function loadNesteRenewalStatus() {
      try {
        const res = await axios.get("/api/admin/neste-renewal");
        if (!ignore) setNesteRenewal(res.data?.renewal || null);
      } catch (err) {
        if (!ignore) setNesteRenewal(null);
      }
    }
    loadNesteSessionStatus();
    loadNesteRenewalStatus();
    const timer = setInterval(loadNesteRenewalStatus, 3000);
    return () => {
      ignore = true;
      clearInterval(timer);
    };
  }, [showPasscodeModal, isAdmin, t]);

  async function runCompare() {
    setError("");
    setOpenIdx(null);
    setData(null);
    if (!postalInput || !litres) {
      setError(t("compare.errors.postalRequired"));
      return;
    }
    if (!selectedFuelType) {
      setError(t("compare.errors.fuelRequired"));
      return;
    }
    const parsedProfit =
      profit === "" ? 0 : parseFloat(profit);
    if (Number.isNaN(parsedProfit) || parsedProfit < 0) {
      setError(t("compare.errors.profitInvalid"));
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/postal-lookup/validate", { postalInput });
      const payload = {
        postalInput,
        litres: parseFloat(litres),
        fuelType: selectedFuelType,
        profitPerUnit: parsedProfit,
      };
      stopComparisonRunPolling();
      const res = await axios.post("/api/comparison-runs", payload);
      applyComparisonRun(res.data, { initialCheck: true });
    } catch (e) {
      console.error(e);
      const serverMessage = e.response?.data?.message;
      if (e.response?.status === 404) {
        setError(serverMessage || t("compare.errors.postalNotFound"));
      } else if (e.response?.status === 400) {
        setError(serverMessage || t("compare.errors.invalidPostal"));
      } else {
        setError(serverMessage || t("compare.errors.compareFailed"));
      }
      if (
        typeof serverMessage === "string" &&
        serverMessage.toLowerCase().includes("abc")
      ) {
        openAbcModal();
      }
      setLoading(false);
      return;
    }
  }

  async function runScrapeNow() {
    if (!data) return;
    const missing = data.items
      .filter((i) => i.unit_price === null)
      .map((i) => i.site);
    if (missing.length === 0) return;

    setScraping(true);
    try {
      const parsedProfit =
        profit === "" ? 0 : parseFloat(profit);
      if (Number.isNaN(parsedProfit) || parsedProfit < 0) {
        setError(t("compare.errors.profitInvalid"));
        setScraping(false);
        return;
      }
      const payload = {
        postalInput: data.postal_code,
        litres: data.litres,
        forceScrapeSites: missing,
        fuelType: data.fuel_type,
        profitPerUnit: parsedProfit,
      };
      const res = await axios.post("/api/compare/corporate", payload);
      setData(res.data);
      if (shouldOpenAbcFromProviderStatuses(res.data?.provider_statuses || [])) {
        openAbcModal();
      }
    } catch (e) {
      console.error(e);
      setError(t("compare.errors.scrapeFailed"));
    } finally {
      setScraping(false);
    }
  }

  function closePasscodeModal() {
    setShowPasscodeModal(false);
    setPasscodeValue("");
    setPasscodeError("");
    setPasscodeSuccess("");
  }

  async function submitPasscode() {
    const trimmed = passcodeValue.trim();
    setPasscodeError("");
    setPasscodeSuccess("");
    setPasscodeResetStatus("");
    if (!/^\d{6}$/.test(trimmed)) {
      setPasscodeError(t("compare.passcode.invalid"));
      return;
    }
    setPasscodeSubmitting(true);
    try {
      await axios.post("/api/admin/neste-passcode", { passcode: trimmed });
      setPasscodeSuccess(t("compare.passcode.success"));
      setPasscodeValue("");
    } catch (err) {
      setPasscodeError(
        err.response?.data?.message || t("compare.passcode.submitError")
      );
    } finally {
      setPasscodeSubmitting(false);
    }
  }

  async function resetNesteSession() {
    if (!window.confirm(t("compare.passcode.resetConfirm"))) {
      return;
    }
    setPasscodeError("");
    setPasscodeSuccess("");
    setPasscodeResetStatus("");
    setPasscodeSessionStatus("");
    setPasscodeResetting(true);
    try {
      const { data } = await axios.post("/api/admin/neste-renewal/start");
      setNesteRenewal(data?.renewal || null);
      setPasscodeResetStatus(t("compare.passcode.resetSuccess"));
    } catch (err) {
      console.error("Failed to reset Neste session", err);
      setPasscodeError(
        err.response?.data?.message || t("compare.passcode.resetError")
      );
    } finally {
      setPasscodeResetting(false);
    }
  }

  const renewalStatusMessage = {
    starting: t("compare.passcode.renewalStarting"),
    waiting_for_code: t("compare.passcode.renewalWaiting"),
    importing: t("compare.passcode.renewalImporting"),
    completed: t("compare.passcode.renewalCompleted"),
    expired: t("compare.passcode.renewalExpired"),
  }[nesteRenewal?.status];
  const renewalActive = ["starting", "waiting_for_code", "importing"].includes(nesteRenewal?.status);
  const cooldownActive = renewalActive && nesteRenewal?.cooldownUntil &&
    new Date(nesteRenewal.cooldownUntil).getTime() > Date.now();

  async function openAbcModal() {
    setShowAbcModal(true);
    setAbcError("");
    setAbcPhoneStatus("");
    setAbcOtpStatus("");
    setAbcLoading(true);
    try {
      const { data } = await axios.get("/api/abc/credentials");
      setAbcPhone(data?.phone || "");
    } catch (err) {
      console.error("Failed to load ABC credentials", err);
      setAbcError(
        err.response?.data?.message || t("compare.abc.loadError")
      );
    } finally {
      setAbcLoading(false);
    }
  }

  function closeAbcModal() {
    setShowAbcModal(false);
    setAbcError("");
    setAbcPhoneStatus("");
    setAbcOtpStatus("");
    setAbcOtp("");
  }

  function stopAbcBatchPolling() {
    if (abcBatchTimer.current) {
      clearTimeout(abcBatchTimer.current);
      abcBatchTimer.current = null;
    }
  }

  function stopComparisonRunPolling() {
    if (comparisonRunTimer.current) {
      clearTimeout(comparisonRunTimer.current);
      comparisonRunTimer.current = null;
    }
  }

  function applyComparisonRun(snapshot, { initialCheck = false } = {}) {
    setComparisonRun(snapshot);
    if (snapshot?.result) setData(snapshot.result);
    if (!showAbcModal && snapshot?.providers?.some((provider) => provider.site === "abc_corp" && provider.state === "waiting_for_otp")) {
      openAbcModal();
    }
    if (["completed", "failed", "cancelled", "expired"].includes(snapshot?.status)) {
      stopComparisonRunPolling();
      setLoading(false);
      return;
    }
    comparisonRunTimer.current = setTimeout(
      () => pollComparisonRun(snapshot.id),
      initialCheck ? 350 : 3000
    );
  }

  async function pollComparisonRun(runId) {
    try {
      const { data: snapshot } = await axios.get(`/api/comparison-runs/${runId}`);
      applyComparisonRun(snapshot);
    } catch (err) {
      stopComparisonRunPolling();
      setLoading(false);
      if (err?.response?.status !== 401) setError(err?.response?.data?.message || t("compare.errors.compareFailed"));
    }
  }

  useEffect(() => {
    return () => {
      stopAbcBatchPolling();
      stopComparisonRunPolling();
    };
  }, []);

  const abcRunProvider = comparisonRun?.providers?.find((provider) => provider.site === "abc_corp");
  const abcOtpSecondsLeft = abcRunProvider?.otpExpiresAt
    ? Math.max(0, Math.ceil((new Date(abcRunProvider.otpExpiresAt).getTime() - comparisonClock) / 1000))
    : null;

  useEffect(() => {
    if (abcRunProvider?.state !== "waiting_for_otp" || !abcRunProvider.otpExpiresAt) return undefined;
    const timer = setInterval(() => setComparisonClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [abcRunProvider?.state, abcRunProvider?.otpExpiresAt]);

  async function pollAbcBatchStatus() {
    try {
      const res = await axios.get("/api/abc/batch/status");
      const status = res.data?.status || "unknown";
      setAbcBatchStatus(status);
      if (status === "running") {
        const total = res.data?.total_targets;
        const success = res.data?.success_count;
        if (typeof total === "number" && typeof success === "number") {
          setAbcBatchProgress(`${success}/${total}`);
        } else {
          setAbcBatchProgress("");
        }
        abcBatchTimer.current = setTimeout(pollAbcBatchStatus, 3000);
      } else if (status === "success") {
        const total = res.data?.total_targets;
        const success = res.data?.success_count;
        if (typeof total === "number" && typeof success === "number") {
          setAbcBatchMessage(`${t("compare.batch.fetching")} (${success}/${total}) ✅`);
        } else {
          setAbcBatchMessage(t("compare.batch.successFallback"));
        }
        setAbcBatchProgress("");
      } else if (status === "failed") {
        const details = res.data?.failure_reason || res.data?.message;
        setAbcBatchMessage(details || t("compare.batch.error"));
        setAbcBatchProgress("");
      } else {
        setAbcBatchProgress("");
      }
    } catch (err) {
      setAbcBatchMessage(
        err.response?.data?.message || err.response?.data?.details || t("compare.batch.error")
      );
      setAbcBatchProgress("");
    }
  }

  async function handleAbcBatchFetch() {
    setAbcBatchMessage("");
    setAbcError("");
    setAbcBatchLoading(true);
    setAbcBatchStatus("running");
    setAbcBatchProgress("");
    try {
      const res = await axios.post("/api/abc/batch/run");
      setAbcBatchMessage(res.data?.message || t("compare.batch.fetching"));
      setPendingAbcBatch(false);
      stopAbcBatchPolling();
      abcBatchTimer.current = setTimeout(pollAbcBatchStatus, 2000);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.details ||
        t("compare.batch.error");
      setAbcBatchMessage(message);
      setAbcBatchStatus(null);
      setAbcBatchProgress("");
      if (message.toLowerCase().includes("koodi") || message.toLowerCase().includes("otp")) {
        openAbcModal();
        setPendingAbcBatch(true);
      }
    } finally {
      setAbcBatchLoading(false);
    }
  }

  async function saveAbcPhone() {
    setAbcError("");
    setAbcPhoneStatus("");
    try {
      await axios.post("/api/abc/phone", { phone: abcPhone });
      setAbcPhoneStatus(t("compare.abc.phoneSaved"));
    } catch (err) {
      console.error("Failed to save ABC phone", err);
      setAbcError(
        err.response?.data?.message ||
          t("compare.abc.phoneSaveError")
      );
    }
  }

  async function submitAbcOtp() {
    setAbcError("");
    setAbcOtpStatus("");
    const trimmed = abcOtp.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setAbcError(t("compare.abc.codeInvalid"));
      return;
    }
    try {
      if (comparisonRun?.id && comparisonRun.providers?.some((provider) => provider.site === "abc_corp" && provider.state === "waiting_for_otp")) {
        const { data: snapshot } = await axios.post(`/api/comparison-runs/${comparisonRun.id}/abc-otp`, { otp: trimmed });
        applyComparisonRun(snapshot, { initialCheck: true });
      } else {
        await axios.post("/api/abc/otp", { otp: trimmed });
      }
      setAbcOtpStatus(t("compare.abc.otpSaved"));
      setAbcOtp("");
      if (pendingAbcBatch) {
        await handleAbcBatchFetch();
        setPendingAbcBatch(false);
      }
    } catch (err) {
      console.error("Failed to submit ABC OTP", err);
      setAbcError(
        err.response?.data?.message || t("compare.abc.otpSaveError")
      );
    }
  }

  const items = data?.items || [];
  const providerStatuses = data?.provider_statuses || [];
  const failedProviders = providerStatuses.filter((entry) => entry?.state === "failed");
  const noDataProviders = providerStatuses.filter((entry) => entry?.state === "no_data");
  const successProviders = providerStatuses.filter((entry) => entry?.state === "success");
  const failedProviderNames = failedProviders.map((entry) => SITE_LABELS[entry.site] || entry.site);
  const successProviderNames = successProviders.map((entry) => SITE_LABELS[entry.site] || entry.site);
  const failedProviderSummary = failedProviders.length
    ? failedProviders.map((entry) => {
        const providerName = SITE_LABELS[entry.site] || entry.site;
        const baseMessage =
          entry.message || t("compare.providerStatus.fetchFailed", { provider: providerName });
        return successProviderNames.length
          ? `${baseMessage} ${t("compare.providerStatus.showingSuccess", {
              providers: formatProviderList(successProviderNames),
            })}`
          : baseMessage;
      })
    : [];
  const noDataSummary = noDataProviders.length
    ? noDataProviders.map((entry) =>
        t("compare.providerStatus.noData", {
          provider: SITE_LABELS[entry.site] || entry.site,
        })
      )
    : [];
  const cheapestItem = items.find((it) => it.totalWithTax !== null);
  const cheapestKey = cheapestItem
    ? `${cheapestItem.site}:${cheapestItem.product_number || cheapestItem.product_name || cheapestItem.fuel_type || ""}`
    : null;
  const hasMissing = items.some((it) => it.unit_price === null);
  const currentGroup = fuelGroups.find((group) => group.id === selectedGroupId);
  const currentVariants = currentGroup?.variants || [];
  const appliedProfit =
    items.length > 0 && typeof items[0]?.p === "number"
      ? items[0].p
      : null;

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold">{pageTitle}</h1>
      <div className="flex justify-end gap-2 flex-wrap">
        {isAdmin && (
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-500"
            onClick={() => setShowPasscodeModal(true)}
          >
            {t("compare.buttons.enterNeste")}
          </button>
        )}
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-500"
          onClick={openAbcModal}
        >
          {t("compare.buttons.abcInfo")}
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded hover:bg-amber-500 disabled:opacity-60"
          onClick={handleAbcBatchFetch}
          disabled={abcBatchLoading}
        >
          {abcBatchLoading
            ? t("compare.batch.fetching")
            : t("compare.buttons.fetchList")}
        </button>
      </div>
      {abcBatchStatus === "running" && (
        <p className="text-sm text-gray-600">
          {t("compare.batch.fetching")} {abcBatchProgress ? `(${abcBatchProgress})` : ""}
        </p>
      )}
      {abcBatchMessage && (
        <p className="text-sm text-blue-600">{abcBatchMessage}</p>
      )}

      <div className="bg-white border rounded-xl shadow p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("compare.inputs.postal")}
            </label>
            <input
              type="text"
              value={postalInput}
              onChange={(e) => setPostalInput(e.target.value)}
              placeholder={t("compare.inputs.postalPlaceholder")}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("compare.inputs.litres")}
            </label>
            <input
              type="number"
              value={litres}
              onChange={(e) => setLitres(e.target.value)}
              placeholder={t("compare.inputs.litresPlaceholder")}
              className="w-full border rounded-lg px-3 py-2"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("compare.inputs.category")}
            </label>
            <select
              value={selectedGroupId}
              onChange={(e) => {
                const nextGroup = e.target.value;
                setSelectedGroupId(nextGroup);
                const group = fuelGroups.find((g) => g.id === nextGroup);
                setSelectedFuelType(group?.variants?.[0]?.id || "");
              }}
              className="w-full border rounded-lg px-3 py-2 bg-white"
              disabled={fuelGroups.length === 0}
            >
              {fuelGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("compare.inputs.fuelType")}
            </label>
            <select
              value={selectedFuelType}
              onChange={(e) => setSelectedFuelType(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 bg-white"
              disabled={currentVariants.length === 0}
            >
              {currentVariants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("compare.inputs.profit")}
            </label>
            <input
              type="number"
              value={profit}
              onChange={(e) => setProfit(e.target.value)}
              placeholder={t("compare.inputs.profitPlaceholder")}
              className="w-full border rounded-lg px-3 py-2"
              min="0"
              step="0.001"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={runCompare}
              disabled={loading || !selectedFuelType}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? t("common.loading") : t("compare.actions.compare")}
            </button>
          </div>
        </div>
        {(error || fuelTypeLoadError) && (
          <p className="text-red-500 text-sm">
            {error || fuelTypeLoadError}
          </p>
        )}
        {comparisonRun && !["completed", "failed", "cancelled", "expired"].includes(comparisonRun.status) && (
          <div className="text-sm text-gray-600" aria-live="polite">
            {t("compare.progress.running")} {comparisonRun.providers?.map((provider) => `${SITE_LABELS[provider.site] || provider.site}: ${t(`compare.progress.states.${provider.state}`)}`).join(" · ")}
          </div>
        )}
      </div>

      {data && (
        <div className="space-y-2">
          {failedProviderSummary.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
              {failedProviderSummary.map((message) => (
                <div key={message}>{message}</div>
              ))}
            </div>
          )}
          {noDataSummary.length > 0 && (
            <div className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700 space-y-1">
              {noDataSummary.map((message) => (
                <div key={message}>{message}</div>
              ))}
            </div>
          )}
          <p className="text-sm text-gray-600">
            {t("compare.summary.description", {
              location: data.postal_name || data.postal_code,
              code: data.postal_code,
              litres: data.litres,
              fuel: data.fuel_type_label || data.fuel_type,
            })}
            {Number.isFinite(appliedProfit) && (
              <>
                {" · "}
                <span className="font-semibold">
                  {t("compare.summary.profit", {
                    value: appliedProfit.toFixed(3),
                  })}
                </span>
              </>
            )}
          </p>

          {/* Card grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((it, idx) => {
              const itemKey = `${it.site}:${it.product_number || it.product_name || it.fuel_type || ""}`;
              const isCheapest = it.totalWithTax !== null && itemKey === cheapestKey;
              const litresValue =
                typeof it.litres === "number" ? it.litres : parseFloat(it.litres) || null;
              const profitPerUnit = typeof it.p === "number" ? it.p : 0;
              const deliveryPerUnit =
                typeof it.delivery_price === "number" && litresValue
                  ? it.delivery_price / litresValue
                  : 0;
              const unitPriceWithProfit =
                it.unit_price !== null
                  ? it.unit_price + profitPerUnit + deliveryPerUnit
                  : null;
              const unitPriceWithProfitInclVat =
                unitPriceWithProfit !== null &&
                typeof it.totalWithTax === "number" &&
                litresValue
                  ? it.totalWithTax / litresValue
                  : null;
              return (
                <div
                  key={itemKey}
                  className={`border rounded-xl p-4 shadow bg-white relative ${
                    isCheapest ? "ring-2 ring-yellow-400" : ""
                  }`}
                >
                  {isCheapest && (
                    <div className="absolute -top-2 right-3 bg-yellow-400 text-xs font-bold px-2 py-0.5 rounded">
                      {t("compare.card.cheapest")}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-500">
                        {it.postal_name || it.postal_code}
                      </div>
                      <div className="text-lg font-semibold">
                        {SITE_LABELS[it.site] || it.site}
                      </div>
                      {(() => {
                        const primaryLabel =
                          it.product_name ||
                          it.fuel_type_label ||
                          it.fuel_type ||
                          "—";
                        const secondaryLabel =
                          it.product_name &&
                          (it.fuel_type_label || it.fuel_type) &&
                          primaryLabel !== (it.fuel_type_label || it.fuel_type)
                            ? it.fuel_type_label || it.fuel_type
                            : null;
                        return (
                          <>
                            <div className="text-xs text-gray-500">
                              {primaryLabel}
                            </div>
                            {secondaryLabel && (
                              <div className="text-xs text-gray-500 italic">
                                {secondaryLabel}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {t("compare.card.totalWithVat")}
                      </div>
                      <div className="text-sm font-semibold">
                        {it.totalWithTax !== null ? `${it.totalWithTax.toFixed(2)} €` : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 grid grid-cols-2 gap-2">
                    <div>
                      {t("compare.card.unitPriceLabel")}:{" "}
                      {it.unit_price !== null
                        ? t("compare.card.unitPriceValue", {
                            value: it.unit_price.toFixed(3),
                          })
                        : "—"}
                      {unitPriceWithProfit !== null && (
                        <div className="text-gray-600 mt-1 space-y-1">
                          <div className="text-sm font-semibold text-black">
                            {t("compare.card.withProfitDeliveryLabel")}
                          </div>
                          <div className="text-lg font-semibold text-black whitespace-nowrap">
                            {t("compare.card.withProfitDeliveryExValue", {
                              value: unitPriceWithProfit.toFixed(3),
                            })}
                          </div>
                          {unitPriceWithProfitInclVat !== null && (
                            <div className="text-lg font-semibold text-black whitespace-nowrap">
                              {t("compare.card.withProfitDeliveryIncValue", {
                                value: unitPriceWithProfitInclVat.toFixed(3),
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>{t("compare.card.litres", { value: it.litres })}</div>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      {openIdx === idx
                        ? t("compare.card.hideDetails")
                        : t("compare.card.showDetails")}
                    </button>
                  </div>

                  {openIdx === idx && (
                    <div className="mt-3 border-t pt-3 text-sm space-y-1">
                      <div>
                        {t("compare.card.fields.postalCode")}: {it.postal_code}
                      </div>
                      <div>
                        {t("compare.card.fields.postalName")}: {it.postal_name || "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.fuelType")}:{" "}
                        {it.fuel_type_label || it.fuel_type || "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.fuelCategory")}:{" "}
                        {it.fuel_category || "—"}
                      </div>
                      {it.product_name && (
                        <div>
                          {t("compare.card.fields.product")}: {it.product_name}
                        </div>
                      )}
                      {it.product_number && (
                        <div>
                          {t("compare.card.fields.productNumber")}: {it.product_number}
                        </div>
                      )}
                      {it.report_date && (
                        <div>
                          {t("compare.card.fields.reportDate")}: {it.report_date}
                        </div>
                      )}
                      <div>
                        {t("compare.card.fields.discount")}:{" "}
                        {typeof it.discount === "number"
                          ? `${it.discount.toFixed(3)} €/L`
                          : "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.delivery")}:{" "}
                        {typeof it.delivery_price === "number"
                          ? `${it.delivery_price.toFixed(2)} €`
                          : "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.fastDelivery")}:{" "}
                        {typeof it.f === "number" ? `${it.f.toFixed(2)} €` : "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.profit")}:{" "}
                        {typeof it.p === "number"
                          ? `${it.p.toFixed(3)} €/L`
                          : "—"}
                      </div>
                      <div>
                        {t("compare.card.fields.totalNoTax")}:{" "}
                        {typeof it.totalNoTax === "number"
                          ? `${it.totalNoTax.toFixed(3)} €`
                          : "—"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showPasscodeModal && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("compare.passcode.title")}</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={closePasscodeModal}
                aria-label={t("compare.accessibility.closePasscodeModal")}
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {t("compare.passcode.description")}
            </p>
            {passcodeSessionStatus && (
              <div className="text-xs text-gray-600">
                {passcodeSessionStatus}
              </div>
            )}
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={passcodeValue}
              onChange={(e) => setPasscodeValue(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("compare.passcode.placeholder")}
            />
            {passcodeError && (
              <div className="text-sm text-red-600">{passcodeError}</div>
            )}
            {passcodeSuccess && (
              <div className="text-sm text-green-600">{passcodeSuccess}</div>
            )}
            {passcodeResetStatus && (
              <div className="text-sm text-green-600">{passcodeResetStatus}</div>
            )}
            {renewalStatusMessage && (
              <div className="text-sm text-gray-700">{renewalStatusMessage}</div>
            )}
            {nesteRenewal?.lastError && (
              <div className="text-sm text-red-600">{nesteRenewal.lastError}</div>
            )}
            {nesteRenewal?.codeRequestedAt && (
              <div className="text-xs text-gray-600">
                {new Date(nesteRenewal.codeRequestedAt).toLocaleString("fi-FI")}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                onClick={closePasscodeModal}
                disabled={passcodeSubmitting}
              >
                {t("common.cancel")}
              </button>
              <button
                className="px-4 py-2 text-sm font-semibold text-gray-700 border rounded hover:bg-gray-50 disabled:opacity-50"
                onClick={resetNesteSession}
                disabled={passcodeSubmitting || passcodeResetting || cooldownActive || nesteRenewal?.status === "importing"}
              >
                {passcodeResetting
                  ? t("common.sending")
                  : renewalActive ? t("compare.passcode.requestAgain") : t("compare.passcode.reset")}
              </button>
              <button
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-500 disabled:opacity-50"
                onClick={submitPasscode}
                disabled={passcodeSubmitting || (renewalActive && nesteRenewal?.status !== "waiting_for_code")}
              >
                {passcodeSubmitting
                  ? t("compare.passcode.sending")
                  : t("compare.passcode.submit")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAbcModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("compare.abc.title")}</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={closeAbcModal}
                aria-label={t("compare.accessibility.closeAbcModal")}
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {t("compare.abc.description")} {t("compare.abc.note")}
            </p>
            {abcRunProvider?.state === "waiting_for_otp" && abcOtpSecondsLeft !== null && (
              <p className="text-sm font-medium text-amber-700">
                {t("compare.abc.expiresIn", { seconds: abcOtpSecondsLeft })}
              </p>
            )}
            {abcRunProvider?.state === "expired" && (
              <p className="text-sm font-medium text-red-600">{t("compare.abc.expired")}</p>
            )}
            {abcError && (
              <div className="text-sm text-red-600">{abcError}</div>
            )}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t("compare.abc.phoneLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={abcPhone}
                  onChange={(e) => setAbcPhone(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2"
                  placeholder={t("compare.abc.phonePlaceholder")}
                  disabled={abcLoading}
                />
                <button
                  className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded hover:bg-green-500 disabled:opacity-60"
                  onClick={saveAbcPhone}
                  disabled={abcLoading}
                >
                  {t("compare.abc.savePhone")}
                </button>
              </div>
              {abcPhoneStatus && (
                <div className="text-sm text-green-600">{abcPhoneStatus}</div>
              )}
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t("compare.abc.codeLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={abcOtp}
                  onChange={(e) => setAbcOtp(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2"
                  placeholder={t("compare.passcode.placeholder")}
                  disabled={abcRunProvider?.state === "expired"}
                />
                <button
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-500"
                  onClick={submitAbcOtp}
                  disabled={abcRunProvider?.state === "expired"}
                >
                  {t("compare.abc.submit")}
                </button>
              </div>
              {abcOtpStatus && (
                <div className="text-sm text-green-600">{abcOtpStatus}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
